import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Home, MessageCircle, Mic, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { getHome } from '../../../api/home';
import type { HomeResponse } from '../../../api/types';
import { useAnonymousUser } from '../../../hooks/useAnonymousUser';
import HomeScreen from './HomeScreen';
import ChatScreen from './ChatScreen';
import CreateMemoryScreen from './CreateMemoryScreen';
import HistoryScreen from './HistoryScreen';
import AnalyticsScreen from './AnalyticsScreen';
import CalendarScreen from './CalendarScreen';
import ProfileScreen from './ProfileScreen';
import PullToRefresh from './PullToRefresh';

type TabId = 'home' | 'chat' | 'create' | 'history' | 'profile';
type SubScreen = 'analytics' | 'calendar' | null;

const TAB_HEIGHT = 80;
const TAB_BAR_HEIGHT = `calc(var(--omut-tab-height, ${TAB_HEIGHT}px) + env(safe-area-inset-bottom))`;

interface AppShellProps {
  initialHomeData?: HomeResponse | null;
}

export default function AppShell({ initialHomeData = null }: AppShellProps) {
  const [activeTab, setActiveTab] = useState<TabId>('home');
  const [subScreen, setSubScreen] = useState<SubScreen>(null);
  const { userId, loading: userLoading, error: userError } = useAnonymousUser();
  const [homeData, setHomeData] = useState<HomeResponse | null>(initialHomeData);
  const [homeLoading, setHomeLoading] = useState(false);
  const [homeError, setHomeError] = useState<string | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [historyMemoryId, setHistoryMemoryId] = useState<string | null>(null);

  const refreshHome = useCallback(() => {
    if (!userId) return Promise.resolve();

    setHomeLoading(true);
    setHomeError(null);

    return getHome(userId)
      .then((data) => {
        setHomeData(data);
        setHomeLoading(false);
      })
      .catch((error) => {
        setHomeError(error instanceof Error ? error.message : 'Не удалось загрузить главную');
        setHomeLoading(false);
      });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setHomeLoading(true);
    setHomeError(null);

    getHome(userId)
      .then((data) => {
        if (cancelled) return;
        setHomeData(data);
        setHomeLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setHomeError(error instanceof Error ? error.message : 'Не удалось загрузить главную');
        setHomeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: 'var(--omut-app-height, 100dvh)',
        minHeight: 'var(--omut-app-height, 100dvh)',
        overflow: 'hidden',
        background: 'linear-gradient(160deg, #1A1B2E 0%, #2D1B3D 100%)',
        isolation: 'isolate',
      }}
    >
      {/* ── Content area (everything above tab bar) ── */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: TAB_BAR_HEIGHT,
          // No overflow here — each screen manages its own scroll
          overflow: 'hidden',
        }}
      >
        {/* Scrollable screens — each wrapped in its own overflowY: auto layer */}
        {activeTab === 'home' && subScreen === null && (
          <PullToRefresh
            onRefresh={refreshHome}
            disabled={userLoading || homeLoading || !userId}
            style={{ position: 'absolute', inset: 0 }}
          >
            <HomeScreen
              data={homeData}
              loading={userLoading || homeLoading}
              error={userError || homeError}
              onOpenAnalytics={() => setSubScreen('analytics')}
              onOpenCalendar={() => setSubScreen('calendar')}
              onCreateMemory={() => setActiveTab('create')}
              onOpenMemory={(memoryId) => {
                setHistoryMemoryId(memoryId);
                setActiveTab('history');
                setSubScreen(null);
              }}
            />
          </PullToRefresh>
        )}
        {activeTab === 'home' && subScreen === 'analytics' && (
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
            <AnalyticsScreen onBack={() => setSubScreen(null)} />
          </div>
        )}
        {activeTab === 'home' && subScreen === 'calendar' && (
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
            <CalendarScreen onBack={() => setSubScreen(null)} />
          </div>
        )}

        {/* Chat — always mounted, hidden when inactive, manages its own internal scroll */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: activeTab === 'chat' ? 'flex' : 'none',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ChatScreen />
        </div>

        {activeTab === 'create' && (
          <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', overflowX: 'hidden' }}>
            <CreateMemoryScreen
              onSaved={() => {
                refreshHome();
                setHistoryRefreshKey((key) => key + 1);
              }}
            />
          </div>
        )}
        {activeTab === 'history' && (
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
            <HistoryScreen
              refreshKey={historyRefreshKey}
              initialMemoryId={historyMemoryId}
              onInitialMemoryHandled={() => setHistoryMemoryId(null)}
            />
          </div>
        )}
        {activeTab === 'profile' && (
          <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', overflowX: 'hidden' }}>
            <ProfileScreen
              onMemoryChanged={() => {
                refreshHome();
                setHistoryRefreshKey((key) => key + 1);
              }}
            />
          </div>
        )}
      </div>

      {/* ── Bottom Tab Bar ── */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: TAB_BAR_HEIGHT,
          minHeight: TAB_BAR_HEIGHT,
          zIndex: 500,
          background: 'rgba(20, 21, 38, 0.97)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          backdropFilter: 'blur(30px)',
          WebkitBackdropFilter: 'blur(30px)',
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          alignItems: 'center',
          paddingBottom: 'env(safe-area-inset-bottom)',
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
          willChange: 'transform',
          color: '#6B7A94',
        }}
      >
        <TabItem
          icon={Home}
          label="Главная"
          active={activeTab === 'home'}
          onClick={() => { setActiveTab('home'); setSubScreen(null); }}
        />
        <TabItem
          icon={MessageCircle}
          label="Чат"
          active={activeTab === 'chat'}
          onClick={() => setActiveTab('chat')}
        />

        {/* Center create button */}
        <button
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            color: '#fff',
            appearance: 'none',
            WebkitAppearance: 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
          onClick={() => setActiveTab('create')}
        >
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #3ECFA0, #2BA87F)',
              boxShadow: '0 8px 24px rgba(62,207,160,0.45)',
              transform: 'translateZ(0)',
            }}
          >
            <Mic size={25} strokeWidth={2.4} color="#fff" style={{ display: 'block' }} />
          </div>
        </button>

        <TabItem
          icon={BookOpen}
          label="История"
          active={activeTab === 'history'}
          onClick={() => setActiveTab('history')}
        />
        <TabItem
          icon={User}
          label="Профиль"
          active={activeTab === 'profile'}
          onClick={() => setActiveTab('profile')}
        />
      </div>
    </div>
  );
}

interface TabItemProps {
  icon: LucideIcon;
  label: string;
  active: boolean;
  onClick: () => void;
}

function TabItem({ icon: Icon, label, active, onClick }: TabItemProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '3px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        color: active ? '#3ECFA0' : '#6B7A94',
        appearance: 'none',
        WebkitAppearance: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon
          size={21}
          strokeWidth={active ? 2.6 : 2.1}
          color="currentColor"
          fill={active ? 'rgba(62,207,160,0.12)' : 'none'}
          style={{ display: 'block', opacity: 1 }}
        />
      </div>
      <span style={{ fontSize: '10px', fontWeight: 500, color: 'currentColor', opacity: 1 }}>
        {label}
      </span>
    </button>
  );
}

function HistoryPlaceholder() {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        padding: '0 32px',
      }}
    >
      <div
        style={{
          width: '64px', height: '64px', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(74,144,226,0.15)',
        }}
      >
        <i className="ri-book-2-line text-3xl" style={{ color: '#4A90E2' }} />
      </div>
      <p style={{ textAlign: 'center', fontSize: '18px', fontWeight: 600, color: '#F5F5F7' }}>
        История воспоминаний
      </p>
      <p style={{ textAlign: 'center', fontSize: '14px', color: '#6B7A94', lineHeight: 1.6 }}>
        Все ваши воспоминания собраны здесь в одном месте
      </p>
    </div>
  );
}

function ProfilePlaceholder() {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        padding: '0 32px',
      }}
    >
      <div
        style={{
          width: '64px', height: '64px', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(139,122,184,0.15)',
        }}
      >
        <i className="ri-user-3-line text-3xl" style={{ color: '#8B7AB8' }} />
      </div>
      <p style={{ textAlign: 'center', fontSize: '18px', fontWeight: 600, color: '#F5F5F7' }}>
        Профиль
      </p>
      <p style={{ textAlign: 'center', fontSize: '14px', color: '#6B7A94', lineHeight: 1.6 }}>
        Ваша личная биография и настройки
      </p>
    </div>
  );
}
